import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, ShieldCheck, ShieldSlash, UserCircleMinus, UserCirclePlus } from "@phosphor-icons/react";

export default function AdminUsers() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/users", { params: { q } }); setItems(data.items); }
        catch (e) { toast.error("Failed to load users"); }
        finally { setLoading(false); }
    }, [q]);
    useEffect(() => { load(); }, [load]);

    const toggleAdmin = async (u) => {
        if (!window.confirm(`${u.is_admin ? "Demote" : "Promote"} ${u.email}?`)) return;
        try { await api.post(`/admin/users/${u.user_id}/toggle-admin`); toast.success("Updated"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const toggleActive = async (u) => {
        if (!window.confirm(`${u.is_active ? "Deactivate" : "Reactivate"} ${u.email}?`)) return;
        try { await api.post(`/admin/users/${u.user_id}/toggle-active`); toast.success("Updated"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input data-testid="u-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="w-full pl-9 pr-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A]" />
                </div>
            </div>

            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b-2 border-[#BFD3FF] text-xs uppercase tracking-widest font-bold text-slate-600">{loading ? "Loading…" : `${items.length} user(s)`}</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
                            <tr><th className="px-4 py-2 font-bold">User</th><th className="px-4 py-2 font-bold">Email</th><th className="px-4 py-2 font-bold">MCQ</th><th className="px-4 py-2 font-bold">Mains</th><th className="px-4 py-2 font-bold">Role</th><th className="px-4 py-2 font-bold">Status</th><th className="px-4 py-2 font-bold text-right w-32">Actions</th></tr>
                        </thead>
                        <tbody>
                            {items.map((u) => (
                                <tr key={u.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-3 text-xs">
                                        <div className="flex items-center gap-2">
                                            {u.picture && <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />}
                                            <span className="font-medium">{u.name || "—"}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-600">{u.email}</td>
                                    <td className="px-4 py-3 text-xs tabular-nums">{u.mcq_attempts}</td>
                                    <td className="px-4 py-3 text-xs tabular-nums">{u.mains_submissions}</td>
                                    <td className="px-4 py-3 text-xs">
                                        {u.is_admin ? <span className="px-1.5 py-0.5 rounded bg-[#1E3A8A]/10 text-[#1E3A8A] font-bold text-[10px]">ADMIN</span> : <span className="text-slate-400 text-[10px]">USER</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {u.is_active === false ? <span className="text-red-600 font-semibold text-[10px]">DISABLED</span> : <span className="text-emerald-700 font-semibold text-[10px]">ACTIVE</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => toggleAdmin(u)} title={u.is_admin ? "Demote" : "Promote"} className="p-1.5 text-slate-500 hover:text-[#1E3A8A]" data-testid={`u-role-${u.user_id}`}>
                                            {u.is_admin ? <ShieldSlash size={14} /> : <ShieldCheck size={14} />}
                                        </button>
                                        <button onClick={() => toggleActive(u)} title={u.is_active === false ? "Reactivate" : "Deactivate"} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`u-active-${u.user_id}`}>
                                            {u.is_active === false ? <UserCirclePlus size={14} /> : <UserCircleMinus size={14} />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No users found.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
